import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RefreshButton from '../../../components/fields/buttons/refresh-button';
import TableTab from "../../../components/tabs/table-tab";
import { REPORTS_FILTER_DATE_TIME_CURRENT_WEEK } from "../../../constants/element-type";
import { FILTER_DATE } from "../../../constants/filter-types";
import { DEF_PG_SIZE_HOURS_WORKED } from '../../../constants/maximum-page-size';
import { HOURS_WORKED } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { ColumnFormattingClasses } from "../../../models/ColumnDefinitions/constants/ColumnFormattingClasses";
import HoursWorkedReportColumn from "../../../models/ColumnDefinitions/HoursWorkedReportColumn";
import { Filter } from "../../../models/Filter";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import AccessAreaSearch from "../../../models/search/AccessAreaSearch";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import HoursWorkedReportSearch from "../../../models/search/HoursWorkedReportSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { getAccessAreaNamesWithPagination } from "../../../services/accessAreaService";
import { auditRunReport } from "../../../services/auditService";
import { getBuildingNamesWithPagination } from "../../../services/buildingService";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getControllerNamesWithPagnation } from "../../../services/controllerService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getFloorsNamesWithPagination } from "../../../services/floorService";
import { getReaderNamesWithPagination } from "../../../services/readerService";
import { getHoursWorkedReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { getZoneNamesWithPagination } from "../../../services/zoneService";
import { getCurrentWeekDates } from "../../../utilities/DateUtils";

interface HoursWorkedReportProps {
    reportId: string;
}

const HoursWorkedReport: FC<HoursWorkedReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const [refreshReport, setRefreshReport] = useState(true);
    const lookup: Lookup[] = [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: "profile",
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "transacksForTrMasterId.zone",
            dataMethod: getZoneNamesWithPagination,
            values: [],
            searchAccessor: "transacksForTrMasterId.zone.id"
        },
        {
            name: "transacksForTrMasterId.controller",
            dataMethod: getControllerNamesWithPagnation,
            values: [],
            searchAccessor: "transacksForTrMasterId.controller.id"
        },
        {
            name: "transacksForTrMasterId.terminal",
            dataMethod: getReaderNamesWithPagination,
            values: [],
            searchAccessor: "transacksForTrMasterId.terminal.id"
        },
        {
            name: "transacksForTrMasterId.building",
            dataMethod: getBuildingNamesWithPagination,
            values: [],
            searchAccessor: "transacksForTrMasterId.building.id"
        },
        {
            name: "transacksForTrMasterId.floor",
            dataMethod: getFloorsNamesWithPagination,
            values: [],
            searchAccessor: "transacksForTrMasterId.floor.id"
        }
    ]

    useEffect(() => {
        if (refreshReport) {
            dataRefresh(getDataMethod);
        }
        setRefreshReport(false);
    }, [refreshReport])

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = HOURS_WORKED;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        const staticFilterKeys = ["dateOnly"];
        const date = new Date();

        //Add custom filter
        let customFilter: Filter[] = [];
        getUserFiltersByView(userLoginId, currentMenuDetail, reportId).then((response) => {
            const dateFilter = response.find(filter => filter.filterId === FILTER_DATE);
            if (!!!dateFilter) {
                customFilter = [{
                    isCustomFilter: true,
                    isStatic: true,
                    key: "dateOnly",
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        elementSysname: REPORTS_FILTER_DATE_TIME_CURRENT_WEEK,
                        label: translate.byKey(TranslationKeys.currentWeek),
                        value: getCurrentWeekDates(date),
                    }]
                }];
            }
        }).catch((error) => {
            console.log("Failed to load user preferences")
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        })
    }
    // <-- end context

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getHoursWorkedReport(translate, { ...listParameters, pageSize: DEF_PG_SIZE_HOURS_WORKED });
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return (<TableContextProvider
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={HoursWorkedReportColumn}
        searchDescriptor={HoursWorkedReportSearch}
        manualDataRefresh={manualDataRefresh}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        maxPageSize={DEF_PG_SIZE_HOURS_WORKED}
        lookup={lookup}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.hoursWorked)}
            selectRows={false}
            showFilterMenu={true}
            cyName="hoursWorkedReport"
            customRowColourWithRange={{ fieldName: "isDefault", comparisonValue: false, colour1: ColumnFormattingClasses.categoryGrey }}>
            <RefreshButton onRefresh={setRefreshReport} />
        </TableTab>
    </TableContextProvider >
    );
};

export default HoursWorkedReport;
